import { strFromU8, unzipSync } from 'fflate';
import { ScoreParseError } from './errors';
import { childrenNamed, parseXml } from './xml';

/** Upper bound for the unzipped contents of an .mxl file, against zip bombs. */
const MAX_UNZIPPED_BYTES = 50 * 1024 * 1024;

function isZip(bytes: Uint8Array): boolean {
  return bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;
}

function decodeUtf16(bytes: Uint8Array, littleEndian: boolean): string {
  const codes: number[] = [];
  let out = '';
  for (let i = 2; i + 1 < bytes.length; i += 2) {
    const lo = bytes[i] as number;
    const hi = bytes[i + 1] as number;
    codes.push(littleEndian ? lo | (hi << 8) : (lo << 8) | hi);
    if (codes.length === 8192) {
      out += String.fromCharCode(...codes);
      codes.length = 0;
    }
  }
  return out + String.fromCharCode(...codes);
}

/** Decode XML bytes, honouring a UTF-8 or UTF-16 byte order mark. */
export function decodeXmlBytes(bytes: Uint8Array): string {
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) return decodeUtf16(bytes, true);
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) return decodeUtf16(bytes, false);
  const start = bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf ? 3 : 0;
  return strFromU8(start ? bytes.subarray(start) : bytes);
}

function unzipMxl(bytes: Uint8Array): Record<string, Uint8Array> {
  let total = 0;
  try {
    return unzipSync(bytes, {
      filter: (file) => {
        total += file.originalSize;
        if (total > MAX_UNZIPPED_BYTES) {
          throw new ScoreParseError('mxlTooLarge', 'The .mxl file unzips to more than 50 MB');
        }
        return true;
      },
    });
  } catch (e) {
    if (e instanceof ScoreParseError) throw e;
    throw new ScoreParseError('mxlInvalid', 'The file is not a valid .mxl (zip) archive');
  }
}

/** Find the score inside a compressed MusicXML (.mxl) archive. */
export function extractMxl(bytes: Uint8Array): string {
  const files = unzipMxl(bytes);
  const container = files['META-INF/container.xml'];
  let rootPath: string | undefined;
  if (container) {
    const tree = parseXml(decodeXmlBytes(container));
    const rootfiles = childrenNamed(childrenNamed(tree[0], 'rootfiles')[0], 'rootfile');
    const musicXmlRoot =
      rootfiles.find((r) => !r.attrs['media-type'] || r.attrs['media-type'].includes('musicxml')) ?? rootfiles[0];
    rootPath = musicXmlRoot?.attrs['full-path'];
  }
  if (!rootPath || !files[rootPath]) {
    rootPath = Object.keys(files)
      .sort()
      .find((p) => !p.startsWith('META-INF/') && /\.(musicxml|xml)$/i.test(p));
  }
  const root = rootPath ? files[rootPath] : undefined;
  if (!root) throw new ScoreParseError('mxlNoRootfile', 'The .mxl archive contains no MusicXML score');
  return decodeXmlBytes(root);
}

/** Turn uploaded bytes (.musicxml, .xml or .mxl) into MusicXML text. */
export function scoreBytesToXml(bytes: Uint8Array): string {
  if (bytes.length === 0) throw new ScoreParseError('emptyFile', 'The file is empty');
  return isZip(bytes) ? extractMxl(bytes) : decodeXmlBytes(bytes);
}
